import { compact, pluralize } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useMemo, useState } from 'react';

import { useKaiProApiState, useScanSynchronizer, useSyncScanProgress } from '@kaiizen/kai-pro-api-react-state';

import { CloudSyncIcon } from '@kaiizen/kai-shiki/react';

import { SyncManagementModal } from '../Partials';
import { COLORS2, TYPOGRAPHY2 } from '../Styles';
import { OfflineIndicatorIcon } from './OfflineIndicatorIcons';

const SYNC_STATUS_INDICATOR_STYLE = css`
  label: SYNC_STATUS_INDICATOR_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  position: relative;
  cursor: pointer;
  user-select: none;
`;

const PENDING_BADGE_BASE_STYLE = css`
  label: PENDING_BADGE_BASE_STYLE;
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS2.alwaysDark.white};
  border-radius: 999px;
  ${TYPOGRAPHY2.xSmall}
  line-height: 1;
`;

const PENDING_BADGE_RUNNING_STYLE = css`
  background: ${COLORS2.alwaysDark.darkGreen};
`;

const PENDING_BADGE_PAUSED_STYLE = css`
  background: ${COLORS2.alwaysDark.yellow};
  color: ${COLORS2.alwaysDark.black};
`;

export const SyncingStatusIndicator = () => {
  const { syncStatus, scanDataSyncStatus, photosSyncStatus, notesSyncStatus } = useSyncScanProgress();
  const { isOffline } = useKaiProApiState();
  const { isPaused } = useScanSynchronizer();
  const [modalVisible, setModalVisible] = useState(false);

  const title = useMemo(() => {
    if (isOffline) return 'You are currently offline.';
    const pausedPrefix = isPaused ? ['Syncing is paused.'] : [];
    return compact([
      ...pausedPrefix,
      scanDataSyncStatus.pending > 0 ? `${pluralize(scanDataSyncStatus.pending, 'scan part')} to sync.` : undefined,
      photosSyncStatus.pending > 0 ? `${pluralize(photosSyncStatus.pending, 'scan photo')} to sync.` : undefined,
      notesSyncStatus.pending > 0 ? `${pluralize(notesSyncStatus.pending, 'scan note')} to sync.` : undefined,
    ]).join('\n');
  }, [isOffline, isPaused, scanDataSyncStatus.pending, photosSyncStatus.pending, notesSyncStatus.pending]);

  return (
    <>
      <div
        css={SYNC_STATUS_INDICATOR_STYLE}
        title={title}
        onClick={() => setModalVisible(true)}
      >
        {isOffline
          ? <OfflineIndicatorIcon />
          : (
            <CloudSyncIcon
              size={32}
              color={isPaused ? COLORS2.alwaysDark.yellow : COLORS2.alwaysDark.white}
            />
          )}
        {syncStatus.pending > 0 && (
          <div css={[PENDING_BADGE_BASE_STYLE, isPaused ? PENDING_BADGE_PAUSED_STYLE : PENDING_BADGE_RUNNING_STYLE]}>
            {syncStatus.pending > 99 ? '99+' : syncStatus.pending}
          </div>
        )}
      </div>
      <SyncManagementModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
      />
    </>
  );
};
